<template>
  <div class="flex flex-col items-center">
    <div v-if="state === 'ONLINE'" class="font-bold text-green-500"><i class="icon-check mr-1"></i>Online</div>
    <div v-if="state === 'OFFLINE'" class="font-bold text-red-500"><i class="icon-x mr-1"></i>Offline</div>
    <div v-if="state === 'NOT_PROGRAMMED'" class="font-bold text-gray-500">No Device Programmed</div>
    <div
      v-if="state === 'NOT_INSTALLED'"
      class="font-bold text-gray-500 bg-gray-100 px-3 rounded-md border-2 border-gray-200"
    >
      Not Installed
    </div>
    <div
      v-if="state === 'NOT_READ'"
      class="font-bold text-gray-500 bg-gray-100 px-3 rounded-md border-2 border-gray-200"
    >
      Not Visited
    </div>
    <div class="relative text-sm text-gray-500 font-medium">
      <div v-if="syncStatus === 'SYNCING'" class="flex items-center">
        Syncing
        <IonSpinner name="dots" color="light" class="absolute -right-8" />
      </div>
      <div v-else-if="syncStatus === 'COMPLETE'" class="flex items-center font-medium">Sync Complete!</div>
      <div v-else-if="syncStatus === 'FAILED'" class="flex items-center font-medium">Sync Failed</div>
      <div v-else-if="lastSyncedAtFormatted">
        {{ isSyncableDevice ? 'Synced' : isManuallyRead ? 'Read collected' : 'Checked in' }}
        {{ lastSyncedAtFormatted }}
      </div>
      <div v-else-if="state !== 'NOT_PROGRAMMED'" class="italic">No reads synced up yet...</div>
      <div v-if="isSyncableDevice && syncStatus === 'IDLE'" class="absolute top-0 bottom-0 -right-10 flex items-center">
        <BaseClearButton @click="syncGateway()" :disabled="!lastSyncedAt" size="small">
          <i class="icon-refresh-cw text-lg"></i>
        </BaseClearButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { formatTimeAgo } from '@vueuse/core';
import { computed } from 'vue';
import { Device, Gateway, IGatewayConnectionStatus, IsOnlineDeviceRead } from '@ncss/models/dist/v3';
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import { isManualReadDevice } from '@/utils/device.utils';
import { IonSpinner } from '@ionic/vue';
import { useGatewaySync } from '@/composables/use-gateway-sync';

export type DeviceSyncStatus = 'IDLE' | 'SYNCING' | 'COMPLETE' | 'FAILED';
export type ManualReadState = 'NOT_READ' | 'READ';
export type DeviceState = 'ONLINE' | 'OFFLINE' | 'NOT_INSTALLED' | 'NOT_PROGRAMMED';

const props = defineProps<{ device?: Device; syncStatus?: DeviceSyncStatus }>();
const emit = defineEmits<{ (e: 'onSyncComplete'): void }>();
const { syncStatus, sync } = useGatewaySync();
const state = computed<DeviceState | ManualReadState>(() => {
  if (props.device instanceof Gateway) {
    return getGatewayState(props.device.id, props.device.lastReadsSyncedUpAt, props.device.computeGatewayStatus());
  } else if (isManualReadDevice(props.device)) {
    return getManualReadState(props.device);
  } else {
    return getStateForDevice(props.device);
  }
});
const lastSyncedAt = computed(() => {
  return props.device instanceof Gateway && props.device.lastReadsSyncedUpAt
    ? props.device.lastReadsSyncedUpAt
    : props.device?.data?.lastCheckIn;
});
const lastSyncedAtFormatted = computed(() => {
  if (lastSyncedAt.value) {
    return formatTimeAgo(new Date(lastSyncedAt.value));
  }
  return undefined;
});
const isSyncableDevice = computed(() => props.device instanceof Gateway);
const isManuallyRead = computed(() => isManualReadDevice(props.device));

function getGatewayState(id?: number, syncedAt?: Date, status?: IGatewayConnectionStatus): DeviceState {
  if (!id) {
    return 'NOT_PROGRAMMED';
  }
  if (status?.isOnline) {
    return 'ONLINE';
  }
  if (!syncedAt) {
    return 'NOT_INSTALLED';
  }
  return 'OFFLINE';
}

function getManualReadState(device: Device): ManualReadState {
  if (!device.data) {
    return 'NOT_READ';
  }
  return 'READ';
}

function getStateForDevice(device?: Device): DeviceState {
  if (!device || !device.id) {
    return 'NOT_PROGRAMMED';
  }
  if (!device.data) {
    return 'NOT_INSTALLED';
  }
  if (IsOnlineDeviceRead(device.data) && device.data.isOnline()) {
    return 'ONLINE';
  }
  return 'OFFLINE';
}

async function syncGateway() {
  const success = await sync(props.device?.id);
  if (success) {
    emit('onSyncComplete');
  }
}
</script>
